/**
 * Zod Validation Schemas
 *
 * Shared validation schemas used across both client-side forms and
 * server-side Edge Functions. Import from this file to ensure
 * client and server validation rules stay in sync.
 */

import { z } from "zod";

// ─── Auth Schemas ─────────────────────────────────────────────────────────────

export const loginSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  password: z
    .string()
    .min(1, "Password is required")
    .min(6, "Password must be at least 6 characters"),
});

export const signUpSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required")
    .max(50, "First name must be 50 characters or fewer"),
  lastName: z
    .string()
    .min(1, "Last name is required")
    .max(50, "Last name must be 50 characters or fewer"),
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  phone: z
    .string()
    .optional()
    .refine(
      (v) => !v || /^[\+\d\s\-\(\)]{7,20}$/.test(v),
      "Please enter a valid phone number"
    ),
  role: z.enum(["strata_admin", "property_manager", "host", "individual"], {
    required_error: "Please select your role",
  }),
  password: z
    .string()
    .min(6, "Password must be at least 6 characters")
    .max(128, "Password is too long"),
});

export const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
});

// ─── Booking Schemas ──────────────────────────────────────────────────────────

export const createBookingSchema = z
  .object({
    host_id: z.string().uuid("Invalid host"),
    unit_id: z.string().uuid("Invalid unit"),
    guest_name: z
      .string()
      .min(1, "Guest name is required")
      .max(120, "Guest name must be 120 characters or fewer"),
    guest_email: z.string().email("Invalid email").optional().or(z.literal("")),
    guest_phone: z
      .string()
      .optional()
      .refine(
        (v) => !v || /^[\+\d\s\-\(\)]{7,20}$/.test(v),
        "Invalid phone number"
      ),
    guest_count: z
      .number({ invalid_type_error: "Guest count must be a number" })
      .int("Guest count must be a whole number")
      .min(1, "At least 1 guest is required")
      .max(20, "Maximum 20 guests"),
    check_in: z.string().min(1, "Check-in date is required"),
    check_out: z.string().min(1, "Check-out date is required"),
    fee: z.number().min(0, "Fee must be 0 or more"),
  })
  .refine(
    (data) => {
      if (!data.check_in || !data.check_out) return true;
      return new Date(data.check_out) > new Date(data.check_in);
    },
    {
      message: "Check-out must be after check-in",
      path: ["check_out"],
    }
  );

// ─── Host Schemas ─────────────────────────────────────────────────────────────

/** Official BC STR registration number formats: H/PM/ST + 9 digits */
const bcRegistrationRegex = /^(H|PM|ST)\d{9}$/i;

export const addHostSchema = z.object({
  unit_id: z.string().uuid("Please select a unit"),
  registration_number: z
    .string()
    .optional()
    .refine(
      (v) => !v || bcRegistrationRegex.test(v),
      "Format: H + 9 digits (e.g. H123456789), PM + 9 digits, or ST + 9 digits"
    ),
  night_limit: z
    .number({ invalid_type_error: "Night limit must be a number" })
    .int()
    .min(1, "Night limit must be at least 1")
    .max(365, "Night limit cannot exceed 365"),
  host_type: z.enum([
    "principal_residence",
    "non_principal_residence",
    "fractional_ownership",
    "strata_hotel",
    "seasonal_exempt",
  ]),
  business_licence_number: z.string().optional(),
  business_licence_status: z.enum(["valid", "revoked", "not_required", "pending"]),
});

// ─── Strata Config Schema ─────────────────────────────────────────────────────

export const strataConfigSchema = z.object({
  max_nights_per_year: z
    .number()
    .int()
    .min(1, "Must be at least 1")
    .max(365, "Cannot exceed 365"),
  min_stay_nights: z
    .number()
    .int()
    .min(1, "Minimum stay must be at least 1 night"),
  max_guest_count: z
    .number()
    .int()
    .min(1, "Must allow at least 1 guest")
    .max(50, "Cannot exceed 50"),
  fee_type: z.enum(["flat", "percentage", "per_night"]),
  fee_amount: z
    .number()
    .min(0, "Fee must be 0 or more")
    .max(100000, "Fee seems unreasonably high"),
  require_insurance: z.boolean(),
  pm_can_modify_fees: z.boolean(),
  collect_municipal_tax: z.boolean(),
  collect_provincial_tax: z.boolean(),
});

// ─── Unit Schema ──────────────────────────────────────────────────────────────

export const createUnitSchema = z.object({
  unit_number: z
    .string()
    .min(1, "Unit number is required")
    .max(20, "Unit number must be 20 characters or fewer"),
  owner_name: z
    .string()
    .min(1, "Owner name is required")
    .max(120, "Owner name must be 120 characters or fewer"),
  owner_email: z
    .string()
    .email("Invalid email")
    .optional()
    .or(z.literal("")),
  str_approved: z.boolean(),
  status: z.enum(["active", "inactive", "pending", "suspended"]),
});

// ─── Invitation Schema ────────────────────────────────────────────────────────

export const inviteSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  role: z.enum(["property_manager", "strata_admin", "host", "individual"]),
});

// ─── Document Upload Schema ───────────────────────────────────────────────────

const MAX_FILE_SIZE_MB = 10;
const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export const documentUploadSchema = z.object({
  name: z
    .string()
    .min(1, "Document name is required")
    .max(120, "Name must be 120 characters or fewer"),
  expiresAt: z.string().optional(),
  file: z
    .instanceof(File)
    .refine(
      (f) => f.size <= MAX_FILE_SIZE_MB * 1024 * 1024,
      `File must be ${MAX_FILE_SIZE_MB} MB or smaller`
    )
    .refine(
      (f) => ACCEPTED_MIME_TYPES.includes(f.type),
      "Accepted formats: PDF, JPEG, PNG, WEBP, DOC, DOCX"
    ),
});

// ─── Compliance Action Schema ─────────────────────────────────────────────────

export const complianceActionSchema = z.object({
  host_id: z.string().uuid("Invalid host"),
  action_type: z.enum(["notice", "warning", "penalty", "order", "reminder"]),
  description: z
    .string()
    .min(10, "Description must be at least 10 characters")
    .max(2000, "Description must be 2,000 characters or fewer"),
});

// ─── Dispute Schema ───────────────────────────────────────────────────────────

export const createDisputeSchema = z.object({
  reason: z
    .string()
    .min(10, "Please describe the dispute (min 10 characters)")
    .max(2000, "Reason must be 2,000 characters or fewer"),
  booking_id: z.string().uuid().optional(),
});

// ─── Booking Validation Input Schema (used by Edge Function) ──────────────────

export const bookingValidationInputSchema = z.object({
  nights: z.number().int().min(1),
  guestCount: z.number().int().min(1),
  checkIn: z.string().min(1),
  checkOut: z.string().min(1),
  unitNumber: z.string().min(1),
  hostStatus: z.string().min(1),
  hostExpiresAt: z.string().nullable().optional(),
  hostNightsUsed: z.number().int().min(0),
  hostNightLimit: z.number().int().min(1),
  strataConfig: z.object({
    max_nights_per_year: z.number().int().min(1),
    min_stay_nights: z.number().int().min(1),
    max_guest_count: z.number().int().min(1),
    require_insurance: z.boolean(),
    fee_type: z.enum(["flat", "percentage", "per_night"]),
    fee_amount: z.number().min(0),
    approved_units: z.array(z.string()),
  }),
});

// ─── Type Exports ─────────────────────────────────────────────────────────────

export type LoginFormData = z.infer<typeof loginSchema>;
export type SignUpFormData = z.infer<typeof signUpSchema>;
export type CreateBookingFormData = z.infer<typeof createBookingSchema>;
export type AddHostFormData = z.infer<typeof addHostSchema>;
export type StrataConfigFormData = z.infer<typeof strataConfigSchema>;
export type CreateUnitFormData = z.infer<typeof createUnitSchema>;
export type InviteFormData = z.infer<typeof inviteSchema>;
export type DocumentUploadFormData = z.infer<typeof documentUploadSchema>;
export type ComplianceActionFormData = z.infer<typeof complianceActionSchema>;
export type CreateDisputeFormData = z.infer<typeof createDisputeSchema>;
export type BookingValidationInput = z.infer<typeof bookingValidationInputSchema>;
